import { Link, useParams } from "react-router-dom";
import ShareButton from "@/components/feature/ShareButton";
import CharacterCard from "@/components/feature/CharacterCard";
import { getCharacterById, getRelatedCharacters } from "@/utils/content";

export default function CharacterDetail() {
  const { id } = useParams();
  const character = getCharacterById(id);

  if (!character) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该角色</h1>
        <p className="mt-2 text-sm text-foreground-600">该角色资料可能已调整，请返回角色大全重新浏览。</p>
        <Link
          to="/characters"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          返回角色大全
        </Link>
      </div>
    );
  }

  const related = getRelatedCharacters(character, 4);

  const meta = [
    { label: "身份分类", value: character.category, icon: "ri-shield-user-line" },
    { label: "所属村落", value: character.village, icon: "ri-map-pin-line" },
    { label: "忍者级别", value: character.rank, icon: "ri-award-line" },
    { label: "忍术类型", value: character.ninjaType, icon: "ri-fire-line" },
  ];

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/characters" className="hover:text-primary-600">角色大全</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{character.name}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[380px_1fr]">
          <div>
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={character.avatar}
                alt={`${character.name} 角色立绘`}
                title={`火影忍者角色 ${character.name}`}
                className="h-80 w-full object-cover object-top md:h-96"
              />
            </div>
            <dl className="mt-5 space-y-3 rounded-lg border border-background-200 bg-background-50 p-5">
              {meta.map((row) => (
                <div key={row.label} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={row.icon} />
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground-900">{row.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                  <i className="ri-emotion-happy-line" />
                  {character.epithet}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  {character.name}
                </h1>
                <p className="mt-1 text-sm text-foreground-500">{character.nameJp}</p>
              </div>
              <ShareButton title={`火影忍者角色资料：${character.name}`} />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {character.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <section className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">角色简介</h2>
              <div className="mt-3 space-y-4">
                <p className="text-sm leading-loose text-foreground-700 md:text-base">
                  <strong className="text-foreground-950">{character.name}</strong>
                  {character.intro}
                </p>
                {character.bio.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 12)}
                    className="text-sm leading-loose text-foreground-700 md:text-base"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">能力解析</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {character.abilities.map((ability) => (
                  <div
                    key={ability.name}
                    className="rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-primary-700">
                      <i className="ri-flashlight-line" />
                      {ability.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {ability.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-links-line" />
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">相关角色推荐</h2>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((item) => (
                <CharacterCard key={item.id} character={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}